import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  FaUserCircle,
  FaUserPlus,
  FaSignOutAlt,
  FaUser,
  FaUserShield,
} from "react-icons/fa"; // 관리자 아이콘 추가
import { auth, db } from "../../firebase";
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import styles from "./Header.module.css";
import dadaLogo from "../../assets/images/dadaLogo.png";

const Header = ({ scrollToSection = {} }) => {
  const [scrolled, setScrolled] = useState(false);
  const [user, setUser] = useState(null);
  const [isVerified, setIsVerified] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Firebase 로그인 상태 감지 및 Firestore에서 역할 확인
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser && currentUser.emailVerified) {
        setUser(currentUser);
        setIsVerified(true);

        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        setIsAdmin(userDoc.exists() && userDoc.data().role === "admin");
      } else {
        setUser(null);
        setIsVerified(false);
        setIsAdmin(false);
      }
    });
    return () => unsubscribe();
  }, []);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/");
    } catch (error) {
      console.error("로그아웃 실패:", error.message);
    }
  };

  // 페이지 이동 후 해당 섹션으로 자동 스크롤
  const handleScrollTo = (sectionRef, sectionPath) => {
    if (location.pathname === "/") {
      // 현재 페이지가 홈이면 즉시 스크롤 이동
      if (sectionRef?.current) {
        const headerHeight =
          document.querySelector(`.${styles.header}`)?.offsetHeight || 70;
        window.scrollTo({
          top: sectionRef.current.offsetTop - headerHeight - 20,
          behavior: "smooth",
        });
      } else {
        console.warn("❌ 이동할 섹션이 없습니다.");
      }
    } else {
      // 현재 페이지가 홈이 아니라면 먼저 홈으로 이동 후 스크롤
      navigate("/", { state: { targetSection: sectionPath } });
    }
  };

  // location 변경 감지 후 자동 스크롤
  useEffect(() => {
    if (
      location.state?.targetSection &&
      scrollToSection[location.state.targetSection]?.current
    ) {
      const headerHeight =
        document.querySelector(`.${styles.header}`)?.offsetHeight || 70;
      setTimeout(() => {
        window.scrollTo({
          top:
            scrollToSection[location.state.targetSection].current.offsetTop -
            headerHeight -
            20,
          behavior: "smooth",
        });
      }, 300);
    }
  }, [location, scrollToSection]);

  const handleLogoClick = () => {
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  return (
    <header className={`${styles.header} ${scrolled ? styles.scrolled : ""}`}>
      <div className={styles.logo} onClick={handleLogoClick}>
        <img src={dadaLogo} alt="dadaLogo" />
      </div>

      <nav className={styles.nav}>
        <span
          onClick={() =>
            handleScrollTo(scrollToSection.tradedStocks, "tradedStocks")
          }
        >
          Traded Stocks
        </span>
        <span
          onClick={() =>
            handleScrollTo(scrollToSection.stockPrices, "stockPrices")
          }
        >
          Stock Prices
        </span>
        <span
          onClick={() =>
            handleScrollTo(scrollToSection.relatedVideos, "relatedVideos")
          }
        >
          Related Videos
        </span>
        <span
          onClick={() => handleScrollTo(scrollToSection.dadachain, "dadachain")}
        >
          Dadachain
        </span>
        <span
          onClick={() =>
            handleScrollTo(scrollToSection.marketSketch, "marketSketch")
          }
        >
          Market Sketch
        </span>
        <span
          onClick={() => handleScrollTo(scrollToSection.library, "library")}
        >
          Library
        </span>
        <span
          onClick={() => handleScrollTo(scrollToSection.partners, "partners")}
        >
          Partners
        </span>
      </nav>

      <div className={styles.actions}>
        {user && isVerified ? (
          <>
            <FaSignOutAlt
              className={styles.icon}
              onClick={handleLogout}
              title="로그아웃"
            />
            {isAdmin ? (
              <FaUserShield
                className={styles.icon}
                onClick={() => navigate("/admin")}
                title="관리자 페이지"
              />
            ) : (
              <FaUser
                className={styles.icon}
                onClick={() => navigate("/profile")}
                title="내 정보"
              />
            )}
          </>
        ) : (
          <>
            <FaUserPlus
              className={styles.icon}
              onClick={() => navigate("/register")}
              title="회원가입"
            />
            <FaUserCircle
              className={styles.icon}
              onClick={() => navigate("/login")}
              title="로그인"
            />
          </>
        )}
      </div>
    </header>
  );
};

export default Header;
